'use client';

import type { Cart } from '@repo/shopify';
import { useCallback, useState } from 'react';
import { type OptimisticProductInfo, useCartActions } from '@/components/cart/cart-provider';
import { CHECKOUT_FALLBACK_PATH } from '@/lib/analytics/checkout-attributes';
import { attachCheckoutAnalyticsAttribution, buildCartEvent, trackAnalyticsEvent } from '@/lib/analytics/client';
import type { BundleEntry } from './bundle-context';
import type { BundleSettings } from './bundle-types';
function generateBundleId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `bundle-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
}
interface AddBundleOptions {
  /** Mobile flow: skip the drawer and go straight to Shopify checkout. */
  redirectToCheckout?: boolean;
}

/**
 * Adds every bundle entry to the cart in ONE batched mutation via
 * `claimRewardItems` (which forwards line attributes — `addItem` does not), tags
 * each line with the `_cart_bundle` attribute, and either opens the cart drawer
 * (desktop) or redirects to Shopify checkout (mobile). The Shopify automatic
 * discount is the source of truth and applies through checkout with no code.
 */
export function useBundleCheckout(settings: BundleSettings) {
  const {
    claimRewardItems,
    suspendPackageProtectionSync
  } = useCartActions();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const addBundle = useCallback(async (entries: BundleEntry[], options?: AddBundleOptions): Promise<Cart | null> => {
    if (entries.length === 0 || isSubmitting) return null;
    setIsSubmitting(true);
    try {
      const bundleId = generateBundleId();
      const attributeKey = settings.discount.lineAttributeKey;
      const lineAttributes = [{
        key: attributeKey,
        value: bundleId
      }];
      const lines = entries.map(entry => {
        const productInfo: OptimisticProductInfo = {
          productId: entry.productId,
          title: entry.title,
          handle: entry.handle,
          vendor: entry.vendor,
          productType: entry.productType,
          image: entry.image,
          variantTitle: entry.variantTitle,
          sku: entry.sku,
          price: {
            amount: String(entry.unitAmount),
            currencyCode: entry.currencyCode
          },
          attributes: lineAttributes
        };
        return {
          merchandiseId: entry.variantId,
          quantity: 1,
          attributes: lineAttributes,
          productInfo
        };
      });

      // Avoid the package-protection reconcile racing the bundle add when we
      // immediately leave for checkout (matches the cart drawer's guard).
      if (options?.redirectToCheckout) {
        suspendPackageProtectionSync();
      }
      const cart = await claimRewardItems(lines, {
        analyticsSource: 'bundle_builder',
        openCart: !options?.redirectToCheckout
      });
      if (cart && options?.redirectToCheckout) {
        // Mirror the cart drawer's checkout handoff: write the Meta/PostHog
        // attribution cart attributes and fire begin_checkout before leaving.
        // Attribution retries briefly, then /api/checkout performs a
        // server-side final attempt before redirecting.
        const checkoutCart = await attachCheckoutAnalyticsAttribution(cart.id);
        trackAnalyticsEvent(buildCartEvent('begin_checkout', checkoutCart ?? cart, 'bundle_builder'));
        window.location.href = checkoutCart?.checkoutUrl || CHECKOUT_FALLBACK_PATH;
      }
      return cart;
    } finally {
      setIsSubmitting(false);
    }
  }, [claimRewardItems, suspendPackageProtectionSync, settings.discount.lineAttributeKey, isSubmitting]);
  return {
    addBundle,
    isSubmitting
  };
}